import axiosInstance from "api"
import { Endpoint } from "api/enpoints.types"
import { schoolGroupsQueryKey } from "api/reactQuery/queries/schoolGroups"
import { AxiosError, AxiosResponse } from "axios"
import { useMutation, useQueryClient } from "react-query"
import store from "store"
import { setClassList, setPage } from "store/utility/utility.slice"
import { IGroup, IGroupImportResult } from "ts/interfaces/Group"

import {
  ICreateSchoolGroupArgs,
  ICreateSchoolGroupTransactionsArgs,
  ICreateSchoolGroupTransactionsVariables,
  ICreateSchoolGroupVariables,
  IDeleteSchoolGroupArgs,
  IDeleteSchoolGroupVariables,
  IImportSchoolGroupsArgs,
  IImportSchoolGroupsVariables,
  IUpdateSchoolGroupArgs,
  IUpdateSchoolGroupVariables,
} from "./schoolGroups.types"

const schoolGroupUrl = () => Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS

const schoolGroupIdUrl = (groupId: number) =>
  Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS_GROUPID.replace("GROUPID", `${groupId}`)

const schoolGroupTransactionsUrl = (groupId: number) =>
  Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS_GROUPID_TRANSACTIONS.replace(
    "GROUPID",
    `${groupId}`,
  )

// The class Students table caches its roster in Redux (`classList`), mirrored there from
// its own query's `onSuccess` — not read from react-query's `data` directly — and that
// query runs with `cacheTime: 0`, so it's dropped from the react-query cache the instant
// the table unmounts (which it is here: school-group management is a separate page). A
// plain `invalidateQueries` has nothing left to mark stale by the time the table remounts.
// Clearing `classList` directly is the one signal that survives the unmount — the table's
// mount effect reacts to an empty `classList` by refetching. `classList` isn't namespaced
// per classId (only one class's roster is ever shown at a time), so this is safe to clear
// broadly even though this mutation has no specific classId to target.
const resetClassesStudentsCache = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries(["classesStudents"])
  // Dispatched straight to the store singleton, not via `useAppDispatch()` — this file has
  // no guarantee of running under a <Provider> (e.g. existing tests render these mutations'
  // consumers without one), and the app's one real store is this same singleton regardless.
  store.dispatch(setPage(0))
  store.dispatch(setClassList([]))
}

export const useMutationCreateSchoolGroup = (args?: ICreateSchoolGroupArgs) => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<IGroup>,
    AxiosError,
    ICreateSchoolGroupVariables
  >(async ({ data }) => await axiosInstance.post(schoolGroupUrl(), data), {
    ...args?.options,
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries(schoolGroupsQueryKey())
      queryClient.invalidateQueries(["groups"])
      resetClassesStudentsCache(queryClient)
      args?.options?.onSuccess?.(response, variables, context)
    },
  })
}

export const useMutationUpdateSchoolGroup = (args?: IUpdateSchoolGroupArgs) => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<IGroup>,
    AxiosError,
    IUpdateSchoolGroupVariables
  >(
    async ({ groupId, data }) =>
      await axiosInstance.patch(schoolGroupIdUrl(groupId), data),
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries(schoolGroupsQueryKey())
        // See useMutationCreateSchoolGroup — same cross-cache reason.
        queryClient.invalidateQueries(["groups"])
        resetClassesStudentsCache(queryClient)
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}

export const useMutationDeleteSchoolGroup = (args?: IDeleteSchoolGroupArgs) => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<null>,
    AxiosError,
    IDeleteSchoolGroupVariables
  >(
    async ({ groupId }) =>
      await axiosInstance.delete(schoolGroupIdUrl(groupId)),
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries(schoolGroupsQueryKey())
        // See useMutationCreateSchoolGroup — same cross-cache reason.
        queryClient.invalidateQueries(["groups"])
        resetClassesStudentsCache(queryClient)
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}

export const useMutationCreateSchoolGroupTransactions = (
  args?: ICreateSchoolGroupTransactionsArgs,
) =>
  useMutation<
    AxiosResponse<null>,
    AxiosError,
    ICreateSchoolGroupTransactionsVariables
  >(
    async ({ groupId, data }) =>
      await axiosInstance.post(schoolGroupTransactionsUrl(groupId), data),
    args?.options,
  )

export const useMutationImportSchoolGroups = (
  args?: IImportSchoolGroupsArgs,
) => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<IGroupImportResult>,
    AxiosError,
    IImportSchoolGroupsVariables
  >(
    async ({ file, onInvalidRows }) => {
      const formData = new FormData()
      formData.append("file", file)
      return await axiosInstance.post(
        Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS_IMPORT,
        formData,
        {
          params: onInvalidRows
            ? { on_invalid_rows: onInvalidRows }
            : undefined,
        },
      )
    },
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        const wasAbortedDryRun =
          variables.onInvalidRows === "abort" &&
          response.data.has_blocking_errors
        if (!wasAbortedDryRun) {
          queryClient.invalidateQueries(schoolGroupsQueryKey())
        }
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}
